import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Lead, Column, FilterState, PaginationState } from '@/types/leads/Table';
import { optimizedApi } from '@/utils/api/optimized-api';
import { updateLead, updateLeadStatus } from '@/actions/lead-manager';
import { showToast } from '@/components/ui/Toast';
import ColumnsService from '@/utils/columnsCache';
import Button from '@/components/ui/Button';
import FSelect from '@/components/ui/FSelect';
import { getCurrentUserRole, ROLES } from '@/utils/permissions';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    rectIntersection,
    useSensor,
    useSensors,
    PointerSensor,
    KeyboardSensor,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Search,
    Filter,
    Plus,
    Download,
    X,
    LayoutGrid,
    Trash2,
    UserCheck,
    Bell,
    Users,
    ChevronDown,
    RotateCcw,
    Check,
    Calendar
} from 'lucide-react';
import PipelineSearch from './PipelineSearch';
import PipelineFiltersEnhanced from './PipelineFiltersEnhanced';
import RemarksModal from './RemarksModal';
import AddLeadModal from './AddLeadModal';
import { cn } from '@/utils/cn';
import ExportModal from '@/components/ExportModal';
import LeadViewSwitcher from '@/components/LeadViewSwitcher';
import { useUsers } from '@/contexts/UsersContext';

// Sortable Lead Card Component
interface SortableLeadCardProps {
    lead: any;
    onLeadClick?: (lead: any) => void;
    isSelected?: boolean;
    onToggleSelect?: (leadUuid: string, checked: boolean) => void;
    onQuickAction?: (action: string, lead: any) => void;
    isFirstCard?: boolean;
}

const SortableLeadCard: React.FC<SortableLeadCardProps> = React.memo(({
    lead,
    onLeadClick,
    isSelected = false,
    onToggleSelect,
    isFirstCard = false
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: lead.uuid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    // Format date to short format: "Jan 15, 2:30 PM"
    const formatDateShort = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const month = date.toLocaleString('en-US', { month: 'short' });
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            data-tour={isFirstCard ? "lead-card" : undefined}
            className={cn(
                "bg-white border rounded-lg p-4 transition-all cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md group",
                isSelected
                    ? "border-blue-500 bg-green-50 shadow-md"
                    : "border-gray-200",
                isDragging && "opacity-50 shadow-lg rotate-2"
            )}
        >
            {/* Header with phone */}
            <div className="flex items-start justify-between">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onLeadClick?.(lead);
                    }}
                    className="text-gray-900 hover:text-gray-700 font-bold text-sm flex-1 text-left flex items-center gap-2"
                >
                    {lead.phone}
                    {/* Calendar icon if events exist */}
                    {lead.events && Array.isArray(lead.events) && lead.events.length > 0 && (
                        <Calendar
                            className="h-4 w-4 text-brandColor"
                            aria-label={`${lead.events.length} event(s)`}
                        />
                    )}
                </button>

                {/* Commented Quick Actions Menu for now */}
                {/* <div className="relative ml-2">
                    ...
                </div> */}

                {/* Selection Checkbox - Show on hover or when selected */}
                <div
                    data-tour={isFirstCard ? "card-checkbox" : undefined}
                    className={cn(
                        "ml-2 flex-shrink-0 transition-all duration-200",
                        isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    <label className="relative inline-block cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                onToggleSelect?.(lead.uuid, e.target.checked);
                            }}
                            className="sr-only"
                        />
                        <div className={cn(
                            "w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center",
                            isSelected
                                ? "bg-brandColor border-brandColor"
                                : "border-gray-300 hover:border-blue-400 bg-white"
                        )}>
                            {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                    </label>
                </div>
            </div>

            {/* Name - only show if not N/A */}
            {lead.name && lead.name !== 'N/A' && lead.name !== 'NA' && (
                <div className="mb-1 flex items-center gap-2">
                    <p className="text-gray-900 text-xs truncate flex-1" title={lead.name}>
                        {lead.name}
                    </p>
                    {/* Priority/Urgency Indicators */}
                    {lead.is_qualified && (
                        <span className="px-1 py-0.5 text-xs bg-green-100 text-green-700 rounded font-medium" title="Qualified">
                            ✓
                        </span>
                    )}
                    {/* Hot lead indicator - if created in last 24 hours */}
                    {lead.created_date && new Date().getTime() - new Date(lead.created_date).getTime() < 24 * 60 * 60 * 1000 && (
                        <span className="px-1 py-0.5 text-xs bg-red-100 text-red-700 rounded font-medium animate-pulse" title="New Lead">
                            🔥
                        </span>
                    )}
                </div>
            )}

            {/* Source - only show if valid */}
            {(lead.sourceDisplay || lead.sourceOption?.opt_name) && (
                <div className="mb-1">
                    <p className="text-gray-600 text-xs">
                        🏷️ {lead.sourceDisplay || lead.sourceOption?.opt_name}
                    </p>
                </div>
            )}

            {/* Date and Owner in single line */}
            <div className="flex items-center justify-between text-xs text-gray-500 gap-2">
                {/* Latest Incoming Date */}
                <span className="flex-shrink-0">
                    {lead.latest_incoming_date ?
                        `📅 ${formatDateShort(lead.latest_incoming_date)}` : ''}
                </span>

                {/* Owner Badge - only show if assigned */}
                {lead.ownerName && lead.ownerName !== 'Unassigned' && (
                    <span
                        className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium truncate max-w-[120px]"
                        title={lead.ownerName}
                    >
                        👤 {lead.ownerName}
                    </span>
                )}
            </div>
        </div>
    );
});

// Add display name for debugging
SortableLeadCard.displayName = 'SortableLeadCard';

// Droppable Status Column Component
interface DroppableStatusColumnProps {
    status: any;
    leads: any[];
    onLeadClick?: (lead: any) => void;
    selectedRows?: Set<string>;
    onToggleSelect?: (leadUuid: string, checked: boolean) => void;
    onBulkSelectStatus?: (leadUuids: string[], checked: boolean) => void;
    activeDragLead?: any;
    onLoadMore?: (statusUuid: string) => void;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: (statusUuid: string) => void;
    onQuickAction?: (action: string, lead: any) => void;
    onGetStatusCount?: (statusUuid: string) => void;
    statusCount?: number;
    isLoadingCount?: boolean;
    isFirstColumn?: boolean;
}

const DroppableStatusColumn: React.FC<DroppableStatusColumnProps> = ({
    status,
    leads,
    onLeadClick,
    selectedRows = new Set(),
    onToggleSelect,
    onBulkSelectStatus,
    activeDragLead,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
    isCollapsed = false,
    onToggleCollapse,
    onQuickAction,
    onGetStatusCount,
    statusCount,
    isLoadingCount = false,
    isFirstColumn = false
}) => {
    const { setNodeRef, isOver } = useDroppable({ id: status.uuid });

    // Check if all leads in this status are selected
    const allLeadsSelected = leads.length > 0 && leads.every(lead => selectedRows.has(lead.uuid));
    const someLeadsSelected = leads.some(lead => selectedRows.has(lead.uuid));

    const handleSelectAllStatus = (checked: boolean) => {
        const leadUuids = leads.map(lead => lead.uuid);

        if (onBulkSelectStatus) {
            // Use bulk selection if available
            onBulkSelectStatus(leadUuids, checked);
        } else if (onToggleSelect) {
            // Fallback to individual selections (this might not work perfectly due to React batching)
            leadUuids.forEach(leadUuid => {
                const isCurrentlySelected = selectedRows.has(leadUuid);
                if (checked && !isCurrentlySelected) {
                    onToggleSelect(leadUuid, true);
                } else if (!checked && isCurrentlySelected) {
                    onToggleSelect(leadUuid, false);
                }
            });
        }
    };

    // Determine if we should show drop zone styling
    const isDragActive = !!activeDragLead;
    const canAcceptDrop = isDragActive && activeDragLead && !leads.some(lead => lead.uuid === activeDragLead.uuid);

    return (
        <div
            ref={setNodeRef}
            data-tour={isFirstColumn ? "status-column" : undefined}
            className={cn(
                "bg-white rounded-lg border border-gray-200 shadow-sm flex-shrink-0 transition-all duration-300 border-t-4 border-t-brandColor group relative",
                isCollapsed ? "w-16 cursor-pointer" : "min-w-[280px]",
                isDragActive && "ring-2 ring-green-100",
                isOver && canAcceptDrop && "bg-green-50 border-green-400 ring-2 ring-green-300 shadow-md transform scale-[1.02]",
                isOver && !canAcceptDrop && "bg-gray-50 border-gray-300"
            )}
            onClick={isCollapsed ? () => onToggleCollapse?.(status.uuid) : undefined}
        >
            {/* Column Header */}
            <div className={cn(
                "border-b border-gray-200 transition-all",
                isCollapsed ? "p-2" : "p-4"
            )}>
                <div className={cn(
                    "flex items-center justify-between",
                    isCollapsed ? "flex-col gap-1" : "mb-2"
                )}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <h3
                            className={cn(
                                "font-bold text-gray-900 uppercase transition-all",
                                isCollapsed ? "writing-mode-vertical" : "truncate max-w-[200px]"
                            )}
                            title={isCollapsed ? "" : status.name}
                        >{isCollapsed ? status.name.charAt(0) : status.name}</h3>
                        <span className="text-sm text-gray-500 flex-shrink-0">({leads.length})</span>
                    </div>

                    {/* Select All Checkbox - Show on hover or when some leads are selected */}
                    {leads.length > 0 && (
                        <div
                            data-tour={isFirstColumn ? "column-select-all" : undefined}
                            className={cn(
                                "transition-all duration-200",
                                someLeadsSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                            )}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <label className="relative inline-block cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={allLeadsSelected}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleSelectAllStatus(e.target.checked);
                                    }}
                                    className="sr-only"
                                />
                                <div className={cn(
                                    "w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center",
                                    allLeadsSelected
                                        ? "bg-brandColor border-brandColor"
                                        : someLeadsSelected
                                        ? "bg-green-100 border-blue-400"
                                        : "border-gray-300 hover:border-blue-400 bg-white"
                                )}>
                                    {allLeadsSelected ? (
                                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    ) : someLeadsSelected ? (
                                        <div className="w-1.5 h-1.5 bg-brandColor rounded-sm"></div>
                                    ) : null}
                                </div>
                            </label>
                        </div>
                    )}
                </div>

                {/* Compact Count Display */}
                {!isCollapsed && (
                    <div className="mt-2">
                        <div
                            onClick={() => onGetStatusCount?.(status.uuid)}
                            className={cn(
                                "group relative w-full px-2.5 py-1.5 rounded-md transition-all duration-200 cursor-pointer border",
                                isLoadingCount
                                    ? "bg-gray-50 border-gray-200 cursor-not-allowed"
                                    : "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 hover:shadow-sm"
                            )}
                        >
                            <div className="flex items-center justify-between">
                                {/* Left side - Inline count display */}
                                <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-gray-600 font-medium">
                                        Total Leads:
                                    </span>
                                    <span className={cn(
                                        "text-sm font-semibold transition-colors",
                                        statusCount !== undefined
                                            ? "text-brandColor"
                                            : "text-gray-400"
                                    )}>
                                        {isLoadingCount ? (
                                            <div className="flex items-center gap-1">
                                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-brandColor border-t-transparent"></div>
                                                <span className="text-xs text-gray-500">Loading</span>
                                            </div>
                                        ) : (
                                            statusCount !== undefined ? statusCount.toLocaleString() : '...'
                                        )}
                                    </span>
                                </div>

                                {/* Right side - Reload icon (appears on hover) */}
                                {!isLoadingCount && (
                                    <div className={cn(
                                        "transition-all duration-200 p-1 rounded",
                                        "opacity-0 group-hover:opacity-100 group-hover:bg-white group-hover:shadow-sm"
                                    )}>
                                        <RotateCcw className="h-3.5 w-3.5 text-brandColor" />
                                    </div>
                                )}
                            </div>

                            {/* Subtle bottom indicator */}
                            <div className={cn(
                                "absolute bottom-0 left-0 right-0 h-0.5 rounded-b-md transition-all duration-200",
                                statusCount !== undefined
                                    ? "bg-gradient-to-r from-blue-400 to-indigo-400"
                                    : "bg-gray-200"
                            )}></div>
                        </div>
                        {/* Drag-drop hint for first column */}
                        {isFirstColumn && (
                            <div data-tour="drag-drop-hint" className="mt-2 px-2 py-1 text-xs text-gray-500 text-center">
                                Drag cards to move
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Lead Cards */}
            <div className={cn(
                "p-3 space-y-3 overflow-y-auto transition-all duration-300",
                isCollapsed ? "max-h-0 overflow-hidden p-0" : "max-h-[calc(100vh-300px)]"
            )}>
                {/* Drop Zone Placeholder - appears when dragging */}
                {isDragActive && isOver && canAcceptDrop && (
                    <div className="border-2 border-dashed border-blue-400 bg-green-50 rounded-lg p-4 text-center animate-pulse">
                        <div className="text-brandColor text-sm font-medium">
                            Drop {activeDragLead?.name || 'lead'} here
                        </div>
                        <div className="text-brandColor text-xs mt-1">
                            Move to {status.name}
                        </div>
                    </div>
                )}

                {leads.length > 0 ? (
                    <SortableContext
                        items={leads.map(lead => lead.uuid)}
                        strategy={verticalListSortingStrategy}
                    >
                        {leads.map((lead, index) => (
                            <SortableLeadCard
                                key={`${status.uuid}-${lead.uuid}-${index}`}
                                lead={lead}
                                onLeadClick={onLeadClick}
                                isSelected={selectedRows.has(lead.uuid)}
                                onToggleSelect={onToggleSelect}
                                onQuickAction={onQuickAction}
                                isFirstCard={isFirstColumn && index === 0}
                            />
                        ))}
                    </SortableContext>
                ) : (
                    /* Empty Column */
                    <div className={cn(
                        "text-center py-8 transition-all duration-200",
                        isDragActive && isOver && canAcceptDrop && "hidden"
                    )}>
                        {isDragActive && canAcceptDrop ? (
                            /* Drop zone for empty column */
                            <div className="border-2 border-dashed border-blue-400 bg-green-50 rounded-lg p-6 animate-pulse">
                                <div className="text-brandColor text-sm font-medium">
                                    Drop {activeDragLead?.name || 'lead'} here
                                </div>
                                <div className="text-brandColor text-xs mt-1">
                                    Move to {status.name}
                                </div>
                            </div>
                        ) : (
                            /* Clean empty state with collapse option */
                            <div className="flex flex-col items-center justify-center h-40 space-y-3">
                                <div className="w-10 h-10 rounded-full bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center">
                                    <div className="text-gray-400 text-sm">📋</div>
                                </div>
                                <div className="text-center space-y-1">
                                    <p className="text-gray-500 text-sm font-medium">{status.name}</p>
                                    <p className="text-gray-400 text-xs">No leads yet</p>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleCollapse?.(status.uuid);
                                        }}
                                        className="text-gray-400 hover:text-gray-600 text-xs underline transition-colors mt-2"
                                        title="Collapse empty column"
                                    >
                                        Hide column
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Load More Button */}
                {!isCollapsed && hasMore && (
                    <div className="mt-3">
                        <button
                            onClick={() => onLoadMore?.(status.uuid)}
                            disabled={isLoadingMore}
                            className={cn(
                                "w-full py-2 px-4 text-sm font-medium rounded-md transition-all",
                                isLoadingMore
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-green-50 text-brandColor hover:bg-green-100"
                            )}
                        >
                            {isLoadingMore ? (
                                <div className="flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brandColor"></div>
                                    Loading...
                                </div>
                            ) : (
                                `Load More (${leads.length}+)`
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

interface EnhancedLeadsTableProps {
    onRowSelect?: (leadIds: string[]) => void;
    onLeadClick?: (lead: Lead) => void;
    tourButton?: React.ReactNode;
}

export interface EnhancedPipelineTableRef {
    refreshPipeline: () => Promise<void>;
}

const EnhancedLeadsTable = forwardRef<EnhancedPipelineTableRef, EnhancedLeadsTableProps>(({
    onRowSelect,
    onLeadClick,
    tourButton
}, ref) => {
    // State management
    const [leads] = useState<Lead[]>([]);
    const [columns, setColumns] = useState<Column[]>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 15
    });
    const [activeFilters, setActiveFilters] = useState<FilterState>({});
    const [tempFilters, setTempFilters] = useState<FilterState>({}); // For inputs before applying
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [, setSelectAll] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
    const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState(false);
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
    const [isBulkReminderModalOpen, setIsBulkReminderModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedNewOwner, setSelectedNewOwner] = useState<string>('');
    const [ownerChangeRemark, setOwnerChangeRemark] = useState<string>('');
    const [isProcessingBulkAction, setIsProcessingBulkAction] = useState(false);

    // Use shared users context to avoid duplicate API calls
    const { users: usersFromContext } = useUsers();
    const users = usersFromContext as any[];

    // Bulk reminder states
    const [reminderTitle, setReminderTitle] = useState<string>('');
    const [reminderDateTime, setReminderDateTime] = useState<string>('');
    const [reminderAlertBefore, setReminderAlertBefore] = useState<string>('15 min');

    // Refs for scroll handling
    const [columnsLoaded, setColumnsLoaded] = useState(false);

    // Persistent storage keys
    const USER_COLUMN_PREFERENCES_KEY = 'crm_user_column_preferences';
    
    // Helper functions for persistent storage
    const saveColumnPreferencesToStorage = (visibleCols: Set<string>) => {
        try {
            const preferences = {
                visibleColumns: Array.from(visibleCols),
                timestamp: Date.now()
            };
            localStorage.setItem(USER_COLUMN_PREFERENCES_KEY, JSON.stringify(preferences));
            console.log('💾 Column preferences saved to localStorage');
        } catch (error) {
            console.error('❌ Error saving column preferences to localStorage:', error);
        }
    };
    
    const loadColumnPreferencesFromStorage = (): Set<string> | null => {
        try {
            const stored = localStorage.getItem(USER_COLUMN_PREFERENCES_KEY);
            if (!stored) return null;
            
            const preferences = JSON.parse(stored);
            console.log('📥 Column preferences loaded from localStorage');
            return new Set(preferences.visibleColumns);
        } catch (error) {
            console.error('❌ Error loading column preferences from localStorage:', error);
            return null;
        }
    };

    // No longer need the mutation hook

    // Step 1: Fetch columns on mount
    useEffect(() => {
        const fetchColumns = async () => {
            try {
                console.log('🔄 Step 1: Fetching columns...');
                const response = await ColumnsService.getUserColumns();
                console.log('📥 Columns response:', response, response.fromCache ? '(from cache)' : '(from API)');
                
                if (response?.success && Array.isArray(response.columns)) {
                    const cols = response.columns;
                    console.log('✅ API returned:', cols.length, 'columns, selected:', cols.filter(c => c.user_column).length);
                    
                    // Add UTM/FB/LI columns to the columns list for visibility control
                    // These are not stored in DB but available in lead data
                    const additionalColumns = [
                        { col_name: 'is_qualified', col_label: 'Qualified', col_type: 'boolean', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'utm_source', col_label: 'UTM Source', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'utm_campaign', col_label: 'UTM Campaign', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'utm_medium', col_label: 'UTM Medium', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'utm_content', col_label: 'UTM Content', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'utm_term', col_label: 'UTM Term', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'fb_platform', col_label: 'FB Platform', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'fb_ad_name', col_label: 'FB Ad Name', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'fb_campaign_name', col_label: 'FB Campaign', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'li_campaign_name', col_label: 'LI Campaign', col_type: 'text', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'updated_date', col_label: 'Updated Date', col_type: 'dateTime', is_custom: false, col_active: 1, user_column: true }, // This should be visible if in user selection
                    ];
                    
                    // Combine API columns with additional data columns
                    const allColumns = [...cols, ...additionalColumns];
                    
                    // Sort columns according to standard order:
                    // 1. Phone (first, always visible)
                    // 2. All CRM columns in col_order ASC (including custom columns)
                    // 3. UTM columns (after all CRM columns)
                    // 4. FB, LinkedIn params (after UTM)
                    const sortedColumns = allColumns.sort((a, b) => {
                        // Phone always comes first
                        if (a.col_name === 'phone') return -1;
                        if (b.col_name === 'phone') return 1;
                        
                        // Categorize columns
                        const aIsUTM = a.col_name.startsWith('utm_');
                        const bIsUTM = b.col_name.startsWith('utm_');
                        const aIsSocial = a.col_name.startsWith('fb_') || a.col_name.startsWith('li_');
                        const bIsSocial = b.col_name.startsWith('fb_') || b.col_name.startsWith('li_');
                        
                        // Group ordering: CRM columns -> UTM -> Social
                        // If one is UTM/Social and other is not, push UTM/Social to the end
                        if (!aIsUTM && !aIsSocial && (bIsUTM || bIsSocial)) return -1;
                        if ((aIsUTM || aIsSocial) && !bIsUTM && !bIsSocial) return 1;
                        
                        // Within UTM vs Social
                        if (aIsUTM && bIsSocial) return -1;
                        if (aIsSocial && bIsUTM) return 1;
                        
                        // Within CRM columns, sort by col_order
                        if (!aIsUTM && !aIsSocial && !bIsUTM && !bIsSocial) {
                            // First, check if either has col_order
                            const aHasOrder = 'col_order' in a && a.col_order !== undefined && a.col_order !== null;
                            const bHasOrder = 'col_order' in b && b.col_order !== undefined && b.col_order !== null;

                            // If both have col_order, sort by it
                            if (aHasOrder && bHasOrder && 'col_order' in a && 'col_order' in b) {
                                return a.col_order - b.col_order;
                            }

                            // If only one has col_order, it comes first
                            if (aHasOrder && !bHasOrder) return -1;
                            if (!aHasOrder && bHasOrder) return 1;
                            
                            // Special case: updated_date should come right after created_date
                            if (a.col_name === 'created_date' && b.col_name === 'updated_date') return -1;
                            if (a.col_name === 'updated_date' && b.col_name === 'created_date') return 1;
                            
                            // Fallback to alphabetical for columns without col_order
                            return a.col_name.localeCompare(b.col_name);
                        }
                        
                        // Within same group (UTM or Social), sort alphabetically
                        return a.col_name.localeCompare(b.col_name);
                    });
                    
                    // Load column preferences from localStorage or use database selection
                    const storedPreferences = loadColumnPreferencesFromStorage();
                    let initialVisible: Set<string>;
                    
                    if (storedPreferences && storedPreferences.size > 0) {
                        // Use stored preferences from localStorage (highest priority)
                        initialVisible = storedPreferences;
                        console.log('📋 Using localStorage column preferences');
                    } else {
                        // Use database user preferences (user_column: true)
                        const userSelectedColumns = sortedColumns
                            .filter(col => col.user_column === true)
                            .map(col => col.col_name);
                        
                        if (userSelectedColumns.length > 0) {
                            // User has saved preferences in database
                            initialVisible = new Set(userSelectedColumns);
                            console.log('📋 Using database column preferences:', userSelectedColumns.length, 'columns');
                            
                            // Save to localStorage for next time
                            saveColumnPreferencesToStorage(initialVisible);
                        } else {
                            // First time user - show only essential columns
                            const essentialColumns = ['phone', 'name', 'email', 'status', 'source', 'created_date', 'updated_date'];
                            initialVisible = new Set(essentialColumns);
                            console.log('📋 First time user - using default essential columns');
                        }
                    }
                    
                    // Set the columns and mark as loaded
                    setColumns(sortedColumns);
                    setColumnsLoaded(true);
                    console.log('✅ Columns loaded successfully, ready to fetch leads');
                } else {
                    console.error('❌ Invalid columns response:', response);
                }
            } catch (error) {
                console.error('❌ Error fetching columns:', error);
            }
        };
        
        fetchColumns();
    }, []); // Empty dependency array - run only once on mount

    // Step 2: Fetch leads when columns are loaded or when filters/pagination changes
    // Pipeline specific state
    const [pipelineData, setPipelineData] = useState<any>(null);
    const [statusColumns, setStatusColumns] = useState<any[]>([]);
    const [leadsByStatus, setLeadsByStatus] = useState<Record<string, any[]>>({});
    const [loadingMore, setLoadingMore] = useState<Record<string, boolean>>({});
    const [hasMoreLeads, setHasMoreLeads] = useState<Record<string, boolean>>({});
    const [cursorsByStatus, setCursorsByStatus] = useState<Record<string, { lead_id: number; date: string } | null>>({});
    const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(new Set());
    const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
    const [loadingCounts, setLoadingCounts] = useState<Record<string, boolean>>({});
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [pipelineFilters, setPipelineFilters] = useState<any>({});
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);

    // Drag and drop state
    const [activeDragLead, setActiveDragLead] = useState<any>(null);
    const [isRemarksModalOpen, setIsRemarksModalOpen] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState<{
        leadUuid: string;
        fromStatus: string;
        toStatus: string;
        lead: any;
    } | null>(null);
    const [originalLeadsByStatus, setOriginalLeadsByStatus] = useState<Record<string, any[]> | null>(null);
    const [isProcessingStatusChange, setIsProcessingStatusChange] = useState(false);

    // Check if current user is a sales rep (hide owner filter for sales reps)
    const currentUserRole = getCurrentUserRole();
    const isSalesRep = currentUserRole === ROLES.SALES_REP ||
                       currentUserRole === ROLES.SENIOR_SALES_REP ||
                       currentUserRole === ROLES.JUNIOR_SALES_REP;

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // 8px movement required to start drag
            },
        }),
        useSensor(KeyboardSensor)
    );

    const fetchLeads = useCallback(async () => {
        // Only fetch if columns are loaded
        if (!columnsLoaded) {
            console.log('⏳ Waiting for columns to load before fetching leads');
            return;
        }

        console.log('🔄 Step 2: Fetching pipeline data...');

        const payload = {
            limit_per_status: 10, // Leads per status column initially
            owner_filter: (activeFilters.owner as any)?.value || null, // Pass owner filter
            ...pipelineFilters, // Include all filter values
            ...(searchTerm && { search: searchTerm }) // Include search term if present
        };

        console.log('Pipeline - pipelineFilters state:', JSON.stringify(pipelineFilters, null, 2));
        console.log('Pipeline - Final payload being sent:', JSON.stringify(payload, null, 2));

        try {
            setIsLoading(true);
            console.log('🚀 Making pipeline leads API call with payload:', payload);
            const result = await optimizedApi.post('/pipeline_leads/pipeline-leads', payload);
            console.log('📥 Pipeline leads API response:', result);

            if (result.success) {
                console.log('✅ Setting pipeline data');
                setPipelineData(result);
                setStatusColumns((result as any).statuses || []);
                setLeadsByStatus((result as any).leadsByStatus || {});

                // Initialize has more leads tracking
                const hasMore: Record<string, boolean> = {};
                const cursors: Record<string, { lead_id: number; date: string } | null> = {};
                const limitPerStatus = payload.limit_per_status || 10;
                ((result as any).statuses || []).forEach((status: any) => {
                    const statusLeads = (result as any).leadsByStatus?.[status.uuid] || [];
                    // Show "Load More" if we got the full limit (indicates there might be more)
                    hasMore[status.uuid] = statusLeads.length >= limitPerStatus;
                    // Set cursor to last lead ID and date if exists (composite cursor for proper pagination)
                    if (statusLeads.length > 0) {
                        const lastLead = statusLeads[statusLeads.length - 1];
                        cursors[status.uuid] = {
                            lead_id: lastLead.lead_id,
                            date: lastLead.latest_incoming_date
                        };
                    } else {
                        cursors[status.uuid] = null;
                    }
                });
                setHasMoreLeads(hasMore);
                setCursorsByStatus(cursors);
            } else {
                console.log('❌ Pipeline API unsuccessful:', result.message);
            }
        } catch (error) {
            console.error('❌ Error fetching pipeline data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [columnsLoaded, activeFilters, searchTerm, pipelineFilters]);

    // Trigger leads fetch when columns are loaded
    useEffect(() => {
        if (columnsLoaded) {
            console.log('📍 Columns loaded, now fetching pipeline data');
            fetchLeads();
        }
    }, [columnsLoaded, fetchLeads]);

    // Expose refresh method to parent component via ref
    useImperativeHandle(ref, () => ({
        refreshPipeline: async () => {
            console.log('🔄 Manual pipeline refresh triggered from parent');
            await fetchLeads();
        }
    }), [fetchLeads]);

    // Handle search loading state
    useEffect(() => {
        // Reset search loading when data changes (search completed)
        setIsSearching(false);
    }, [pipelineData]);

    // Handle filter changes (immediate input changes)
    const handleFilterChange = (columnName: string, value: any, type: 'text' | 'select' | 'date' | 'dateTime') => {
        if (type === 'date' || type === 'dateTime') {
            // Handle date range filters
            setTempFilters(prev => ({
                ...prev,
                [columnName]: {
                    ...(prev[columnName] as any || {}),
                    ...value,
                    type
                }
            }));
        } else if (type === 'select') {
            // Handle multi-select filters
            setTempFilters(prev => ({
                ...prev,
                [columnName]: { value: Array.isArray(value) ? value : [value].filter(Boolean), type }
            }));
        } else {
            setTempFilters(prev => ({
                ...prev,
                [columnName]: { value, type }
            }));
        }
    };

    // Apply filters (when user confirms)
    const applyFilters = () => {
        setActiveFilters({ ...tempFilters });
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Check if there are filters to apply
    const hasFiltersToApply = useMemo(() => {
        return Object.keys(tempFilters).some(key => {
            const tempValue = tempFilters[key];
            const activeValue = activeFilters[key];

            // Handle array values (multi-select)
            if (Array.isArray((tempValue as any)?.value) && Array.isArray((activeValue as any)?.value)) {
                return JSON.stringify((tempValue as any).value.sort()) !== JSON.stringify((activeValue as any).value.sort());
            }

            // Handle date range values
            if ((tempValue as any)?.type === 'date' || (tempValue as any)?.type === 'dateTime') {
                return (tempValue as any)?.from !== (activeValue as any)?.from || (tempValue as any)?.to !== (activeValue as any)?.to;
            }

            // Handle other values
            return (tempValue as any)?.value !== (activeValue as any)?.value;
        }) || Object.keys(tempFilters).length !== Object.keys(activeFilters).length;
    }, [tempFilters, activeFilters]);

    // Clear all filters
    const clearFilters = () => {
        setActiveFilters({});
        setTempFilters({});
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle row selection
    const handleRowSelect = (leadUuid: string, checked: boolean) => {
        const newSelection = new Set(selectedRows);
        if (checked) {
            newSelection.add(leadUuid);
        } else {
            newSelection.delete(leadUuid);
        }
        setSelectedRows(newSelection);
        setSelectAll(newSelection.size === leads.length && leads.length > 0);

        onRowSelect?.(Array.from(newSelection));
    };

    // Handle bulk status selection
    const handleBulkSelectStatus = (leadUuids: string[], checked: boolean) => {
        const newSelection = new Set(selectedRows);

        if (checked) {
            // Add all leads to selection
            leadUuids.forEach(uuid => newSelection.add(uuid));
        } else {
            // Remove all leads from selection
            leadUuids.forEach(uuid => newSelection.delete(uuid));
        }

        setSelectedRows(newSelection);
        setSelectAll(newSelection.size === leads.length && leads.length > 0);

        onRowSelect?.(Array.from(newSelection));
    };

    // Custom collision detection that only considers status columns as drop targets
    const customCollisionDetection = (args: any) => {
        // First, get intersections using rect intersection
        const intersections = rectIntersection(args);

        // Filter to only include status column UUIDs (droppable zones)
        const validStatusUuids = statusColumns.map(status => status.uuid);
        const validIntersections = intersections.filter((intersection: any) =>
            validStatusUuids.includes(intersection.id)
        );

        // Return the valid intersections, or fall back to the original if none found
        return validIntersections.length > 0 ? validIntersections : intersections;
    };

    // Drag and drop handlers
    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const draggedLead = findLeadById(active.id as string);
        setActiveDragLead(draggedLead);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragLead(null);

        if (!over) return;

        const leadUuid = active.id as string;
        const fromStatusUuid = findLeadStatusUuid(leadUuid);
        const toStatusUuid = over.id as string;

        // With custom collision detection, over.id should always be a status UUID
        // If dropped on the same status, do nothing
        if (fromStatusUuid === toStatusUuid) {
            return;
        }

        const lead = findLeadById(leadUuid);
        if (!lead || !fromStatusUuid) {
            return;
        }

        // Save original state for potential rollback
        setOriginalLeadsByStatus({ ...leadsByStatus });

        // OPTIMISTIC UPDATE: Immediately move the lead to the new column
        const updatedLeadsByStatus = { ...leadsByStatus };

        // Remove from source column
        if (updatedLeadsByStatus[fromStatusUuid]) {
            updatedLeadsByStatus[fromStatusUuid] = updatedLeadsByStatus[fromStatusUuid].filter(
                l => l.uuid !== leadUuid
            );
        }

        // Add to target column at the top
        if (!updatedLeadsByStatus[toStatusUuid]) {
            updatedLeadsByStatus[toStatusUuid] = [];
        }

        // Update the lead's status info for the optimistic update
        const updatedLead = {
            ...lead,
            status_uuid: toStatusUuid,
            statusOption: statusColumns.find(s => s.uuid === toStatusUuid) || lead.statusOption
        };

        updatedLeadsByStatus[toStatusUuid] = [updatedLead, ...updatedLeadsByStatus[toStatusUuid]];

        // Apply the optimistic update
        setLeadsByStatus(updatedLeadsByStatus);

        // Open remarks modal for mandatory remark
        setPendingStatusChange({
            leadUuid,
            fromStatus: fromStatusUuid,
            toStatus: toStatusUuid,
            lead: updatedLead
        });
        setIsRemarksModalOpen(true);
    };

    // Helper function to find lead by UUID
    const findLeadById = (leadUuid: string) => {
        for (const statusUuid in leadsByStatus) {
            const lead = leadsByStatus[statusUuid].find(l => l.uuid === leadUuid);
            if (lead) return lead;
        }
        return null;
    };

    // Helper function to find which status a lead belongs to
    const findLeadStatusUuid = (leadUuid: string) => {
        for (const statusUuid in leadsByStatus) {
            if (leadsByStatus[statusUuid].find(l => l.uuid === leadUuid)) {
                return statusUuid;
            }
        }
        return null;
    };

    // Load more leads for a specific status column using cursor pagination
    const loadMoreLeads = async (statusUuid: string) => {
        if (loadingMore[statusUuid] || !hasMoreLeads[statusUuid]) return;

        // Find the status by UUID to get its ID
        const status = statusColumns.find(s => s.uuid === statusUuid);
        if (!status) return;

        setLoadingMore(prev => ({ ...prev, [statusUuid]: true }));
        try {
            const cursor = cursorsByStatus[statusUuid];
            const payload = {
                status_id: status.id,
                limit: 10, // Load 10 more leads at a time (same as initial load)
                cursor_lead_id: cursor?.lead_id || null,
                cursor_date: cursor?.date || null, // Composite cursor for pagination
            };

            console.log('Loading more leads for status:', statusUuid, payload);
            const response = await optimizedApi.post('/pipeline_leads/status-cursor', payload);

            if (response.success && (response as any).leads) {
                const newLeads = (response as any).leads;
                const pagination = (response as any).pagination;

                // Append new leads to existing ones
                setLeadsByStatus(prev => ({
                    ...prev,
                    [statusUuid]: [...(prev[statusUuid] || []), ...newLeads]
                }));

                // Update cursor using the pagination object from backend (composite cursor)
                // The backend now returns cursor as {lead_id, date} object
                if (pagination?.nextCursor) {
                    setCursorsByStatus(prev => ({
                        ...prev,
                        [statusUuid]: pagination.nextCursor
                    }));

                    // Use hasNext from backend pagination
                    setHasMoreLeads(prev => ({
                        ...prev,
                        [statusUuid]: pagination.hasNext === true
                    }));
                } else {
                    // No more leads or no cursor returned
                    setCursorsByStatus(prev => ({
                        ...prev,
                        [statusUuid]: null
                    }));
                    setHasMoreLeads(prev => ({
                        ...prev,
                        [statusUuid]: false
                    }));
                }
            }
        } catch (error) {
            console.error('Error loading more leads:', error);
            showToast('Failed to load more leads', 'error');
        } finally {
            setLoadingMore(prev => ({ ...prev, [statusUuid]: false }));
        }
    };

    // Auto-load all status counts with single API call
    const loadAllStatusCounts = useCallback(async () => {
        if (statusColumns.length === 0) return;

        console.log('🔢 Loading all status counts with single call...');

        // Set loading state for all statuses
        const loadingStates: Record<string, boolean> = {};
        statusColumns.forEach(status => {
            loadingStates[status.uuid] = true;
        });
        setLoadingCounts(loadingStates);

        // Build payload with all filters (same as pipeline-leads)
        const payload = {
            owner_filter: (activeFilters.owner as any)?.value || null,
            ...pipelineFilters, // Include all filter values (created_date, etc.)
            ...(searchTerm && { search: searchTerm }) // Include search term if present
        };

        console.log('🔢 All status counts payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await optimizedApi.post('/pipeline_leads/all-status-counts', payload);

            if (response.success && (response as any).counts) {
                setStatusCounts((response as any).counts);
                console.log('✅ All status counts loaded:', (response as any).counts);
            }
        } catch (error) {
            console.error('Error loading status counts:', error);
            showToast('Failed to load status counts', 'error');
        } finally {
            setLoadingCounts({});
        }
    }, [statusColumns, activeFilters.owner, pipelineFilters, searchTerm]);

    // Refresh all status counts on demand (triggered by any status refresh button)
    const handleRefreshStatusCount = async (statusUuid: string) => {
        const status = statusColumns.find(s => s.uuid === statusUuid);
        if (!status) return;

        console.log('🔄 Refreshing all status counts via batch endpoint...');
        await loadAllStatusCounts();
        showToast('Status counts refreshed', 'success');
    };

    // Auto-load status counts after pipeline data is loaded or when filters change
    useEffect(() => {
        if (statusColumns.length > 0) {
            // Small delay to let the UI render first
            setTimeout(() => {
                loadAllStatusCounts();
            }, 100);
        }
    }, [statusColumns, loadAllStatusCounts]);

    // Toggle column collapse/expand
    const toggleColumnCollapse = (statusUuid: string) => {
        const newCollapsed = new Set(collapsedColumns);
        if (newCollapsed.has(statusUuid)) {
            newCollapsed.delete(statusUuid);
        } else {
            newCollapsed.add(statusUuid);
        }
        setCollapsedColumns(newCollapsed);
    };

    // Handle quick actions on lead cards
    const handleQuickAction = (action: string, lead: any) => {
        switch (action) {
            case 'call':
                window.open(`tel:${lead.phone}`, '_self');
                break;
            case 'email':
                if (lead.email) {
                    window.open(`mailto:${lead.email}`, '_self');
                } else {
                    showToast('No email address available', 'warning');
                }
                break;
            case 'edit':
                onLeadClick?.(lead);
                break;
            default:
                break;
        }
    };

    // Handle global search
    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setIsSearching(true);
        // fetchLeads will be triggered automatically by useEffect when searchTerm changes
    };

    // Handle filter application
    const handleApplyFilters = (filters: any) => {
        console.log('Pipeline - handleApplyFilters called with:', JSON.stringify(filters, null, 2));

        // Transform date filters from { startDate, endDate } to { from, to }
        const transformedFilters: any = {};
        Object.entries(filters).forEach(([key, value]: [string, any]) => {
            if (value && typeof value === 'object' && (value.startDate || value.endDate)) {
                // Transform date filter format
                transformedFilters[key] = {
                    from: value.startDate,
                    to: value.endDate
                };
            } else {
                transformedFilters[key] = value;
            }
        });

        console.log('Pipeline - Transformed filters:', JSON.stringify(transformedFilters, null, 2));
        setPipelineFilters(transformedFilters);
        // fetchLeads will be triggered automatically by useEffect when pipelineFilters changes
    };

    // Process status change with remark
    const processStatusChange = async (remarkText: string) => {
        if (!pendingStatusChange || !remarkText.trim()) {
            showToast('Please enter a remark', 'warning');
            return;
        }

        setIsProcessingStatusChange(true);

        try {
            const toStatus = statusColumns.find(s => s.uuid === pendingStatusChange.toStatus);
            if (!toStatus) {
                showToast('Target status not found', 'error');
                return;
            }

            // Update lead status via API using the dedicated status update endpoint
            const response = await updateLeadStatus(
                pendingStatusChange.leadUuid,
                toStatus.uuid,
                remarkText
            );

            if (response.success) {
                showToast('Lead status updated successfully', 'success');

                // Clear the original state - optimistic update is now confirmed
                setOriginalLeadsByStatus(null);

                // Optionally refresh status counts to stay in sync
                loadAllStatusCounts();
            } else {
                showToast('Failed to update lead status', 'error');

                // Rollback the optimistic update
                if (originalLeadsByStatus) {
                    setLeadsByStatus(originalLeadsByStatus);
                    setOriginalLeadsByStatus(null);
                }
            }
        } catch (error) {
            console.error('Error updating lead status:', error);
            showToast('An error occurred while updating lead status', 'error');

            // Rollback the optimistic update on error
            if (originalLeadsByStatus) {
                setLeadsByStatus(originalLeadsByStatus);
                setOriginalLeadsByStatus(null);
            }
        } finally {
            // Close modal and reset state
            setIsProcessingStatusChange(false);
            setIsRemarksModalOpen(false);
            setPendingStatusChange(null);
        }
    };

    // Close remarks modal and revert drag
    const handleCloseRemarksModal = () => {
        // Rollback optimistic update
        if (originalLeadsByStatus) {
            setLeadsByStatus(originalLeadsByStatus);
            setOriginalLeadsByStatus(null);
        }

        setIsRemarksModalOpen(false);
        setPendingStatusChange(null);
        setIsProcessingStatusChange(false);
    };


    // Handle bulk change owner
    const handleChangeOwner = () => {
        setIsChangeOwnerModalOpen(true);
        setIsBulkActionsOpen(false);
    };

    // Handle bulk add reminder
    const handleBulkAddReminder = useCallback(() => {
        setIsBulkReminderModalOpen(true);
        setIsBulkActionsOpen(false);
    }, []);

    // Optimized form handlers to prevent input lag
    const handleReminderTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setReminderTitle(e.target.value);
    }, []);

    const handleReminderDateTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setReminderDateTime(e.target.value);
    }, []);

    const handleReminderAlertChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setReminderAlertBefore(e.target.value);
    }, []);

    const handleOwnerChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedNewOwner(e.target.value);
    }, []);

    const handleOwnerRemarkChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setOwnerChangeRemark(e.target.value);
    }, []);

    const handleCloseReminderModal = useCallback(() => {
        setIsBulkReminderModalOpen(false);
        setReminderTitle('');
        setReminderDateTime('');
        setReminderAlertBefore('15 min');
    }, []);

    const handleCloseOwnerModal = useCallback(() => {
        setIsChangeOwnerModalOpen(false);
        setSelectedNewOwner('');
        setOwnerChangeRemark('');
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setIsDeleteConfirmModalOpen(false);
    }, []);

    // Process bulk owner change
    const processBulkOwnerChange = async () => {
        if (!selectedNewOwner || !ownerChangeRemark.trim() || selectedRows.size === 0) {
            showToast('Please select a new owner and add a remark', 'warning');
            return;
        }

        setIsProcessingBulkAction(true);
        try {
            const selectedLeadUuids = Array.from(selectedRows);
            const results = [];

            for (const leadUuid of selectedLeadUuids) {
                try {
                    const leadData = findLeadById(leadUuid);
                    if (leadData) {
                        const response = await updateLead(
                            leadUuid,
                            leadData,
                            ownerChangeRemark,
                            selectedNewOwner
                        );
                        results.push({ uuid: leadUuid, success: response.success });
                    } else {
                        console.warn(`Lead data not found for UUID: ${leadUuid}`);
                        results.push({ uuid: leadUuid, success: false });
                    }
                } catch (error) {
                    console.error(`Error updating lead ${leadUuid}:`, error);
                    results.push({ uuid: leadUuid, success: false });
                }
            }

            const successCount = results.filter(r => r.success).length;
            const failCount = results.filter(r => !r.success).length;

            if (successCount > 0) {
                showToast(`Successfully updated ${successCount} lead(s). ${failCount > 0 ? `${failCount} failed.` : ''}`, 'success');
                // Refresh the pipeline data
                await fetchLeads();
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast('Failed to update leads. Please try again.', 'error');
            }
        } catch (error) {
            console.error('❌ Bulk owner change error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsChangeOwnerModalOpen(false);
            setSelectedNewOwner('');
            setOwnerChangeRemark('');
        }
    };

    // Process bulk add reminder
    const processBulkReminder = async () => {
        if (!reminderTitle.trim() || !reminderDateTime || selectedRows.size === 0) {
            showToast('Please fill in all required fields', 'warning');
            return;
        }

        setIsProcessingBulkAction(true);
        try {
            const selectedLeadUuids = Array.from(selectedRows);

            const response = await optimizedApi.post('/leads/reminder_lead', {
                leadUuid: selectedLeadUuids,
                event_title: reminderTitle,
                daterange: reminderDateTime,
                alert_before: reminderAlertBefore
            });

            if (response.success) {
                showToast(`Successfully added reminders to ${selectedLeadUuids.length} lead(s)`, 'success');
                // Refresh the pipeline data
                await fetchLeads();
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(`Failed to add reminders: ${response.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            console.error('❌ Bulk reminder error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsBulkReminderModalOpen(false);
            setReminderTitle('');
            setReminderDateTime('');
            setReminderAlertBefore('15 min');
        }
    };

    // Handle bulk delete
    const handleBulkDelete = () => {
        setIsDeleteConfirmModalOpen(true);
        setIsBulkActionsOpen(false);
    };

    // Process bulk delete
    const processBulkDelete = async () => {
        if (selectedRows.size === 0) return;

        setIsProcessingBulkAction(true);
        try {
            const selectedLeadUuids = Array.from(selectedRows);

            const response = await optimizedApi.post('/leads/delete_lead', {
                leadUuids: selectedLeadUuids
            });

            if (response.success) {
                showToast(`Successfully deleted ${selectedLeadUuids.length} lead(s)`, 'success');
                // Refresh the pipeline data
                await fetchLeads();
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(`Failed to delete leads: ${response.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            console.error('❌ Bulk delete error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsDeleteConfirmModalOpen(false);
        }
    };

    // Show loading state
    if (isLoading && leads.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading leads...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            {/* Compact Single Line Toolbar */}
            <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
                <div className="px-6 py-3">
                    <div className="flex items-center justify-between">
                        {/* Left Section - Title and Stats */}
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-3">
                                <div data-tour="view-switcher">
                                    <LeadViewSwitcher />
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                                {Object.keys(statusCounts).length > 0 && (
                                    <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md font-medium">
                                        {Object.values(statusCounts).reduce((sum, count) => sum + count, 0)} total
                                    </span>
                                )}
                                {selectedRows.size > 0 && (
                                    <span className="px-3 py-1.5 bg-green-100 text-brandColor rounded-md font-medium">
                                        {selectedRows.size} selected
                                    </span>
                                )}
                            </div>

                            {/* Bulk Actions Dropdown - Show only when items are selected */}
                            {selectedRows.size > 0 && (
                                <div data-tour="bulk-actions" className="relative pl-4 border-l border-gray-400">
                                    <button
                                        onClick={() => setIsBulkActionsOpen(!isBulkActionsOpen)}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor text-white text-sm font-medium rounded-md hover:bg-brandColor transition-colors"
                                    >
                                        <span>Bulk Actions ({selectedRows.size})</span>
                                        <ChevronDown className={`h-4 w-4 transition-transform ${isBulkActionsOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {isBulkActionsOpen && (
                                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                            <div className="py-1">
                                                <button
                                                    onClick={handleChangeOwner}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <UserCheck className="h-4 w-4 text-brandColor" />
                                                    Change Owner
                                                </button>
                                                <button
                                                    onClick={() => setIsExportModalOpen(true)}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <Download className="h-4 w-4 text-gray-600" />
                                                    Export Selected
                                                </button>
                                                <button
                                                    onClick={handleBulkAddReminder}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <Bell className="h-4 w-4 text-amber-600" />
                                                    Add Reminder
                                                </button>
                                                <div className="border-t border-gray-100 my-1"></div>
                                                <button
                                                    onClick={handleBulkDelete}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                                                    <Trash2 className="h-4 w-4" />
                                                    Delete Selected
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right Section - Action Buttons */}
                        <div className="flex items-center gap-3">
                            {/* Owner Filter - Hidden for Sales Reps */}
                            {!isSalesRep && (
                                <div data-tour="owner-filter" className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-gray-500" />
                                    <FSelect
                                        options={[
                                            { uuid: 'unassigned', opt_id: 'unassigned', opt_name: 'Unassigned' },
                                            ...users.map(user => ({
                                                uuid: user.uuid,
                                                opt_id: user.uuid,
                                                opt_name: user.userName
                                            }))
                                        ]}
                                        value={Array.isArray((tempFilters['owner'] as any)?.value) ? (tempFilters['owner'] as any).value : []}
                                        onChange={(selectedValues) => handleFilterChange('owner', selectedValues, 'select')}
                                        placeholder="All Owners"
                                        searchable={true}
                                        showCount={false}
                                        className="min-w-[200px]"
                                        wideDropdown={true}
                                        dropdownClassName="min-w-[250px]"
                                    />
                                </div>
                            )}

                            {/* Filter Controls */}
                            <div className="flex items-center gap-2">
                                <button
                                    data-tour="apply-filters"
                                    onClick={applyFilters}
                                    disabled={!hasFiltersToApply}
                                    className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                                        hasFiltersToApply
                                            ? 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                            : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                    }`}
                                >
                                    <Check className="h-4 w-4" />
                                    <span>Apply</span>
                                    {Object.keys(tempFilters).length > 0 && (
                                        <span className="px-2 py-0.5 text-xs bg-brandColor text-white rounded-full font-bold">
                                            {Object.keys(tempFilters).length}
                                        </span>
                                    )}
                                </button>

                                {Object.keys(activeFilters).length > 0 && (
                                    <button
                                        onClick={clearFilters}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                                        title="Clear all filters"
                                    >
                                        <X className="h-4 w-4" />
                                        Clear
                                    </button>
                                )}
                            </div>

                            <div className="h-8 w-px bg-gray-300"></div>

                            {/* Search Component */}
                            <div data-tour="search">
                                <PipelineSearch
                                    onSearch={handleSearch}
                                    isLoading={isSearching}
                                    currentSearchTerm={searchTerm}
                                    className="min-w-[300px]"
                                />
                            </div>

                            <div className="h-6 w-px bg-gray-300"></div>

                            {/* Filter Button */}
                            <button
                                data-tour="advanced-filters"
                                onClick={() => setShowFilters(true)}
                                className={cn(
                                    "inline-flex items-center justify-center p-2 rounded-lg border transition-all relative",
                                    Object.keys(pipelineFilters).length > 0
                                        ? "bg-green-50 text-brandColor border-blue-200 hover:bg-green-100"
                                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                )}
                                title="Advanced Filters"
                            >
                                <Filter className="h-5 w-5" />
                                {Object.keys(pipelineFilters).length > 0 && (
                                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-brandColor text-white text-xs rounded-full flex items-center justify-center">
                                        {Object.keys(pipelineFilters).length}
                                    </span>
                                )}
                            </button>

                            {/* Primary Actions */}
                            <button
                                data-tour="add-lead"
                                onClick={() => setShowAddLeadModal(true)}
                                className="inline-flex items-center justify-center p-2 bg-brandColor text-white rounded-lg hover:bg-brandColor transition-all hover:shadow-md"
                                title="Add Lead"
                            >
                                <Plus className="h-5 w-5" />
                            </button>

                            <button
                                data-tour="export"
                                onClick={() => setIsExportModalOpen(true)}
                                className="inline-flex items-center justify-center p-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all"
                                title="Export Leads"
                            >
                                <Download className="h-5 w-5" />
                            </button>

                            {/* Tour Button */}
                            {tourButton}
                        </div>
                    </div>

                    {/* Active Filters Row - Compact */}
                    {Object.keys(activeFilters).length > 0 && (
                        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Active Filters:</span>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(activeFilters).map(([key, filter]) => {
                                    // Get display label and value
                                    let displayLabel = key === 'owner' ? 'Owner' : columns.find(col => col.col_name === key)?.col_label || key;
                                    let displayValue = '';

                                    if (filter && key === 'owner' && Array.isArray((filter as any).value)) {
                                        // For owner filter, show user names instead of UUIDs
                                        const selectedOwnerNames = (filter as any).value.map((uuid: string) => {
                                            if (uuid === 'unassigned') return 'Unassigned';
                                            const user = users.find(u => u.uuid === uuid);
                                            return user ? user.userName : uuid;
                                        });
                                        displayValue = selectedOwnerNames.length === 1
                                            ? selectedOwnerNames[0]
                                            : `${selectedOwnerNames.length} selected`;
                                    } else if (filter && Array.isArray((filter as any).value)) {
                                        displayValue = `${(filter as any).value.length} selected`;
                                    } else if (filter) {
                                        displayValue = String((filter as any).value).substring(0, 15);
                                    }

                                    return (
                                        <span
                                            key={key}
                                            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-green-50 text-gray-700 rounded-full border border-blue-200"
                                        >
                                            <span className="font-medium">
                                                {displayLabel}:
                                            </span>
                                            <span className="text-brandColor" title={filter && Array.isArray((filter as any).value) ? (filter as any).value.join(', ') : filter ? String((filter as any).value) : ''}>
                                                {displayValue}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const newFilters = { ...activeFilters };
                                                    delete newFilters[key];
                                                    setActiveFilters(newFilters);
                                                    const newTempFilters = { ...tempFilters };
                                                    delete newTempFilters[key];
                                                    setTempFilters(newTempFilters);
                                                }}
                                                className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Active Search/Filter Indicators */}
            {(searchTerm || Object.keys(pipelineFilters).length > 0) && (
                <div className="mb-4 p-3 bg-green-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-blue-900">Active Filters:</span>
                        <div className="flex items-center gap-2 flex-wrap">
                            {searchTerm && (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-blue-800 text-xs rounded-md">
                                    <Search className="h-3 w-3" />
                                    <span>Search: "{searchTerm}"</span>
                                    <button
                                        onClick={() => handleSearch('')}
                                        className="ml-1 text-brandColor hover:text-blue-800"
                                        title="Clear search"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            )}
                            {Object.keys(pipelineFilters).length > 0 && (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-blue-800 text-xs rounded-md">
                                    <Filter className="h-3 w-3" />
                                    <span>{Object.keys(pipelineFilters).length} filter{Object.keys(pipelineFilters).length !== 1 ? 's' : ''}</span>
                                    <button
                                        onClick={() => handleApplyFilters({})}
                                        className="ml-1 text-brandColor hover:text-blue-800"
                                        title="Clear all filters"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => {
                                handleSearch('');
                                handleApplyFilters({});
                            }}
                            className="ml-auto text-xs text-brandColor hover:text-blue-800 font-medium"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            )}

            {/* Kanban Board */}
            <div className="bg-gray-50 rounded-lg py-4 px-6">
                {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading pipeline data...</p>
                        </div>
                    </div>
                ) : statusColumns.length > 0 ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={customCollisionDetection}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="flex gap-4 overflow-x-auto pb-4">
                            {statusColumns
                                .sort((a, b) => a.order - b.order) // Sort by col_order ASC
                                .map((status, index) => (
                                    <DroppableStatusColumn
                                        key={`status-${status.uuid}`}
                                        status={status}
                                        leads={leadsByStatus[status.uuid] || []}
                                        onLeadClick={onLeadClick}
                                        selectedRows={selectedRows}
                                        onToggleSelect={handleRowSelect}
                                        onBulkSelectStatus={handleBulkSelectStatus}
                                        activeDragLead={activeDragLead}
                                        onLoadMore={loadMoreLeads}
                                        hasMore={hasMoreLeads[status.uuid] || false}
                                        isLoadingMore={loadingMore[status.uuid] || false}
                                        isCollapsed={collapsedColumns.has(status.uuid)}
                                        onToggleCollapse={toggleColumnCollapse}
                                        onQuickAction={handleQuickAction}
                                        onGetStatusCount={handleRefreshStatusCount}
                                        statusCount={statusCounts[status.uuid]}
                                        isLoadingCount={loadingCounts[status.uuid] || false}
                                        isFirstColumn={index === 0}
                                    />
                                ))}
                        </div>

                        {/* Drag Overlay */}
                        <DragOverlay>
                            {activeDragLead ? (
                                <div className="bg-brandColor text-white border border-blue-700 rounded-lg px-4 py-2 shadow-lg opacity-90">
                                    <span className="font-medium text-sm">
                                        {activeDragLead.phone}
                                    </span>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    <div className="text-center py-12">
                        <LayoutGrid className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Pipeline Data</h3>
                        <p className="text-gray-500 mb-4">No status columns found</p>
                        <button
                            onClick={fetchLeads}
                            className="bg-brandColor text-white px-4 py-2 rounded-lg hover:bg-brandColor"
                        >
                            Retry
                        </button>
                    </div>
                )}
            </div>


            {/* Change Owner Modal */}
            {isChangeOwnerModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">Change Owner</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select New Owner
                                </label>
                                <select
                                    value={selectedNewOwner}
                                    onChange={handleOwnerChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                >
                                    <option value="">Select a user...</option>
                                    {users.map((user) => (
                                        <option key={user.uuid} value={user.uuid}>
                                            {user.userName} ({user.userEmail})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Remark (Required)
                                </label>
                                <textarea
                                    key="owner-change-remark"
                                    value={ownerChangeRemark}
                                    onChange={handleOwnerRemarkChange}
                                    rows={3}
                                    placeholder="Enter reason for owner change..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent resize-none"
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                            </div>
                            <p className="text-sm text-gray-600">
                                {selectedRows.size} lead(s) will be assigned to the new owner.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseOwnerModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkOwnerChange}
                                disabled={!selectedNewOwner || !ownerChangeRemark.trim() || isProcessingBulkAction}
                                className="flex-1"
                            >
                                {isProcessingBulkAction ? 'Processing...' : 'Change Owner'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Reminder Modal */}
            {isBulkReminderModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">Add Reminder to {selectedRows.size} Lead(s)</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reminder Title *
                                </label>
                                <input
                                    type="text"
                                    value={reminderTitle}
                                    onChange={handleReminderTitleChange}
                                    placeholder="Enter reminder title..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date & Time *
                                </label>
                                <input
                                    type="datetime-local"
                                    value={reminderDateTime}
                                    onChange={handleReminderDateTimeChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Alert Before
                                </label>
                                <select
                                    value={reminderAlertBefore}
                                    onChange={handleReminderAlertChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                >
                                    <option value="15 min">15 min</option>
                                    <option value="30 min">30 min</option>
                                    <option value="1 hour">1 hour</option>
                                    <option value="2 hours">2 hours</option>
                                    <option value="1 day">1 day</option>
                                    <option value="2 days">2 days</option>
                                </select>
                            </div>
                            <p className="text-sm text-gray-600">
                                A reminder will be added to {selectedRows.size} selected lead(s).
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseReminderModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkReminder}
                                disabled={!reminderTitle.trim() || !reminderDateTime || isProcessingBulkAction}
                                className="flex-1"
                            >
                                {isProcessingBulkAction ? 'Adding...' : 'Add Reminder'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteConfirmModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">Confirm Delete</h3>
                        <p className="text-gray-700 mb-4">
                            Are you sure you want to delete {selectedRows.size} selected lead(s)? This action cannot be undone.
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                            <p className="text-sm text-red-800">
                                ⚠️ This will permanently remove all selected leads and their associated data.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseDeleteModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkDelete}
                                disabled={isProcessingBulkAction}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            >
                                {isProcessingBulkAction ? 'Deleting...' : 'Delete All'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remarks Modal for Status Change */}
            <RemarksModal
                isOpen={isRemarksModalOpen}
                onClose={handleCloseRemarksModal}
                onSubmit={processStatusChange}
                pendingStatusChange={pendingStatusChange}
                statusColumns={statusColumns}
                isProcessing={isProcessingStatusChange}
            />

            {/* Export Modal */}
            <ExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                selectedRows={Array.from(selectedRows)}
                totalFilteredCount={pagination.totalItems}
                currentFilters={{
                    primary_filters: activeFilters,
                    custom_filters: {}
                }}
            />

            {/* Enhanced Pipeline Filters Modal */}
            <PipelineFiltersEnhanced
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                columns={columns.map(col => ({
                    ...col,
                    col_id: String(col.col_id || '')
                }))} // Pass all columns, component will filter internally
                onApplyFilters={handleApplyFilters}
                currentFilters={pipelineFilters}
                isLoading={false}
            />

            {/* Add Lead Modal */}
            <AddLeadModal
                isOpen={showAddLeadModal}
                onClose={() => setShowAddLeadModal(false)}
                onSuccess={() => {
                    // Refresh pipeline data after successful lead creation
                    fetchLeads();
                    loadAllStatusCounts();
                }}
                refreshData={() => {
                    fetchLeads();
                    loadAllStatusCounts();
                }}
            />
        </div>
    );
});

EnhancedLeadsTable.displayName = 'EnhancedLeadsTable';

export default React.memo(EnhancedLeadsTable);